import { CATALOG } from "./catalog.js";
import { QUEST_TEMPLATES } from "./quests.js";
import { TOWN_SQUARE_PROJECT } from "./projects.js";
import { HOME_TYPES } from "./homes.js";
import { PLACEMENT_SCALE_MAX_STEP } from "./types.js";
import type { ActivityBootstrap, Gift, NeighbourHome, PetState, PropertyState, WorldState } from "./types.js";
import { generateWorld } from "./world.js";

const demoMembers = [
  { id: "demo-user", name: "Danny" },
  { id: "member-river", name: "River" },
  { id: "member-juniper", name: "Juniper" },
  { id: "member-mochi", name: "Mochi" },
  { id: "member-sable", name: "Sable" },
  { id: "member-wren", name: "Wren" },
  { id: "member-ember", name: "Ember" },
  { id: "member-olive", name: "Olive" },
  { id: "member-pip", name: "Pip" },
  { id: "member-briar", name: "Briar" },
  { id: "member-maple", name: "Maple" },
  { id: "member-bean", name: "Bean" }
];

export function createDemoWorld(): WorldState {
  const world = generateWorld("tiny-tribe-private-build");
  const orderedPlots = [...world.plots].sort((left, right) =>
    Math.hypot(left.position.x, left.position.z) - Math.hypot(right.position.x, right.position.z)
  );
  const assignments = new Map(orderedPlots.slice(0, demoMembers.length).map((plot, index) => [plot.id, demoMembers[index]!.id]));
  return {
    ...world,
    plots: world.plots.map((plot) => ({ ...plot, assignedOwnerId: assignments.get(plot.id) ?? null }))
  };
}

export function createStarterProperty(world: WorldState, memberId = "demo-user", memberName = "Danny"): PropertyState {
  const plot = world.plots.find((candidate) => candidate.assignedOwnerId === memberId) ?? world.plots[0]!;
  const pets: PetState[] = [
    { id: "pet-mochi", species: "cat", name: "Mochi", appearance: "ginger", affection: 68, slot: 0, unlockedAnimations: ["nap", "wave"] },
    { id: "pet-pip", species: "turtle", name: "Pip", appearance: "moss", affection: 34, slot: 1, unlockedAnimations: ["look"] }
  ];
  return {
    id: `property-${memberId}`,
    ownerId: memberId,
    ownerName: memberName,
    name: "Willow Cottage",
    plotId: plot.id,
    // A fresh member has a parcel and no home on it. The first quest unlocks the three types
    // and the free pick, so the preview build walks the same path a real member does.
    homeType: null,
    plotTier: 0,
    unlockedRooms: ["living"],
    placements: [
      {
        id: "placement-mailbox",
        itemId: "exterior-1",
        surface: "exterior",
        position: { x: 0, y: 0, z: 9 },
        rotationQuarters: 0,
        scaleStep: PLACEMENT_SCALE_MAX_STEP,
        selectedVariants: { finish: "weathered" }
      },
      {
        id: "placement-bird-bath",
        itemId: "exterior-3",
        surface: "exterior",
        position: { x: -8, y: 0, z: 0 },
        rotationQuarters: 1,
        scaleStep: PLACEMENT_SCALE_MAX_STEP,
        selectedVariants: { finish: "weathered" }
      }
    ],
    pets,
    plants: [
      { id: "garden-lavender", itemId: "plant-13", bedIndex: 0, growth: 3, plantedAt: "2026-07-28T13:00:00.000Z" },
      { id: "garden-rosemary", itemId: "plant-20", bedIndex: 1, growth: 2, plantedAt: "2026-07-31T13:00:00.000Z" }
    ],
    version: 1
  };
}

/**
 * The rest of the street, for the preview build.
 *
 * A village where every house but yours is a guess is exactly the thing neighbours exist to fix,
 * so the demo gives each of them a home of their own and something out in the garden. Assigned by
 * position rather than at random, so the preview looks the same every time it is opened.
 */
function createDemoNeighbours(world: WorldState): NeighbourHome[] {
  const decor = ["exterior-1", "exterior-3", "exterior-14"];

  return world.plots
    .filter((plot) => plot.assignedOwnerId && plot.assignedOwnerId !== "demo-user")
    .map((plot, index) => ({
      memberId: plot.assignedOwnerId!,
      displayName: demoMembers.find((member) => member.id === plot.assignedOwnerId)?.name ?? "Tribe member",
      plotId: plot.id,
      homeType: HOME_TYPES[index % HOME_TYPES.length]!.type,
      plotTier: index % 5 === 0 ? 1 : 0,
      placements: [
        {
          id: `${plot.id}-decor-a`,
          itemId: decor[index % decor.length]!,
          surface: "exterior" as const,
          position: { x: 0, y: 0, z: 9 },
          rotationQuarters: (index % 4) as number,
          scaleStep: PLACEMENT_SCALE_MAX_STEP,
          selectedVariants: { finish: index % 2 ? "weathered" : "new" }
        },
        {
          id: `${plot.id}-decor-b`,
          itemId: decor[(index + 1) % decor.length]!,
          surface: "exterior" as const,
          position: { x: -8, y: 0, z: 0 },
          rotationQuarters: ((index + 2) % 4) as number,
          scaleStep: PLACEMENT_SCALE_MAX_STEP,
          selectedVariants: { finish: index % 2 ? "new" : "weathered" }
        }
      ]
    }));
}

const demoGifts: Gift[] = [
  { id: "gift-1", senderId: "member-juniper", recipientId: "demo-user", itemId: "exterior-14", status: "delivered", createdAt: "2026-08-03T18:20:00.000Z" }
];

export function createDemoBootstrap(): ActivityBootstrap {
  const world = createDemoWorld();
  return {
    guild: { id: "guild-demo", name: "Tribe", isModerator: true },
    member: { id: "demo-user", displayName: "Danny" },
    wallet: { copper: 784 },
    property: createStarterProperty(world),
    neighbours: createDemoNeighbours(world),
    world,
    catalog: CATALOG,
    ownedItemIds: [
      "room-living-room", "furniture-hearth-1", "furniture-hearth-2", "furniture-cottage-1",
      "exterior-1", "exterior-3", "exterior-14", "plant-13", "plant-20", "pet-cat", "pet-turtle"
    ],
    activeQuests: [QUEST_TEMPLATES[0]!, QUEST_TEMPLATES[12]!],
    project: TOWN_SQUARE_PROJECT,
    gifts: demoGifts,
    rewardInbox: []
  };
}
